// Keeps the device's scheduled reminders in step with the plan, list, kitchen and settings.

import { planReminders } from '../domain/notifications';
import { track } from '../lib/analytics';
import { notifier, type Permission } from '../lib/notify';
import { durable } from './persistence';
import { useStore } from './store';

let timer: ReturnType<typeof setTimeout> | undefined;
let unsubscribe: (() => void) | null = null;

/** Works out the reminders from the current state and hands them to the device. */
export async function reschedule(): Promise<void> {
  const n = notifier();
  if (!n) return;
  const s = useStore.getState();
  const prefs = s.profile.notifications;
  try {
    const allowed = prefs.enabled && (await n.permission()) === 'granted';
    await n.setReminders(allowed ? planReminders(durable(s), prefs) : []);
  } catch {
    // Scheduling failed (e.g. the plugin is unavailable); try again on the next change.
  }
}

/** On app start: schedule now, again a moment after relevant changes, and open tapped reminders. */
export function initReminders(open: (route: string) => void): void {
  const n = notifier();
  if (!n) return;
  n.onOpen((route) => { track('reminder_opened'); open(route); });
  void reschedule();
  unsubscribe?.();
  let last = '';
  unsubscribe = useStore.subscribe((s) => {
    const key = JSON.stringify([s.profile.notifications, s.plan, s.list, s.kitchen.items, s.onboarded, s.checkIns, s.checkInSnoozedUntil, s.profile.checkInMonths]);
    if (key === last) return;
    last = key;
    clearTimeout(timer);
    timer = setTimeout(() => { void reschedule(); }, 800);
  });
}

/**
 * Turns reminders on: asks the system for permission if it hasn't been asked, and records the answer.
 * Returns the permission, or null where reminders aren't available.
 */
export async function enableReminders(source: 'card' | 'settings'): Promise<Permission | null> {
  const n = notifier();
  if (!n) return null;
  const permission = await n.request();
  const s = useStore.getState();
  s.updateProfile({ notifications: { ...s.profile.notifications, enabled: permission === 'granted', askedAt: s.profile.notifications.askedAt ?? new Date().toISOString() } });
  track('reminders_enabled', { source, permission });
  await reschedule();
  return permission;
}

export function dismissReminderCard(): void {
  const s = useStore.getState();
  s.updateProfile({ notifications: { ...s.profile.notifications, askedAt: new Date().toISOString() } });
  track('reminders_dismissed');
}
